import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Button,
  Col,
  Form,
  Image,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap';
import { LuImageOff, LuSearch } from 'react-icons/lu';
import { toast } from 'react-toastify';
import {
  CreateStoreCarousel,
  UpdateStoreCarousel,
} from '../../Api/apis/carouselApi';
import {
  CreateStoreCarouselPayload,
  StoreCarouselData,
  StoreCarouselImageType,
  UpdateStoreCarouselPayload,
} from '../../Api/apis/types/carousel.types';
import { Search_Store_Product, Store_Product_Details } from '../../Api';
import { STORAGE_CONSTANTS } from '../../shared/Constants/Constants';
import { getImageUrl } from '../../Utils/imageUtils';
import { getCarouselImageUrl } from './StoreCarousel';
import styles from './StoreCarouselForm.style';

/** Fields of a store product (Store_Product_Details / Search_Store_Product) used here */
interface ProductOption {
  id: number;
  product_name: string;
  product_image?: string | null;
  price?: string | number | null;
}

const TYPE_OPTIONS = [
  { value: StoreCarouselImageType.PRODUCT_IMAGE, label: 'Product' },
  { value: StoreCarouselImageType.CAROUSEL_IMAGE, label: 'Manual' },
];

// Mirrors CreateStoreCarouselPayload: a Product carousel needs `product`, a
// Manual one needs `image`. When editing, `$hasCurrentImage` (passed as the
// resolver context) makes the image optional so the current one is kept.
const carouselSchema = yup.object({
  carousel_image_type: yup
    .mixed<StoreCarouselImageType>()
    .oneOf(Object.values(StoreCarouselImageType))
    .required(),
  // Always sent, but may be empty
  title: yup
    .string()
    .trim()
    .defined()
    .max(255, 'Title must be 255 characters or fewer'),
  product: yup
    .number()
    .nullable()
    .when('carousel_image_type', {
      is: StoreCarouselImageType.PRODUCT_IMAGE,
      then: (schema) => schema.required('Please select a product'),
    }),
  image: yup
    .mixed<File>()
    .nullable()
    .when(['carousel_image_type', '$hasCurrentImage'], {
      is: (type: StoreCarouselImageType, hasCurrentImage: boolean) =>
        type === StoreCarouselImageType.CAROUSEL_IMAGE && !hasCurrentImage,
      then: (schema) => schema.required('Please upload an image'),
    }),
  is_active: yup.boolean().required(),
});

type CarouselFormValues = yup.InferType<typeof carouselSchema>;

interface StoreCarouselFormProps {
  /** Carousel to edit; leave undefined to add a new one */
  carousel?: StoreCarouselData;
  onCancel: () => void;
  onSaved: () => void;
}

interface FieldRowProps {
  label: string;
  required?: boolean;
  helper?: React.ReactNode;
  children: React.ReactNode;
}

const FieldRow: React.FC<FieldRowProps> = ({
  label,
  required,
  helper,
  children,
}) => (
  <Row className="py-3 border-bottom g-2">
    <Col md={3}>
      <span style={styles.label}>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </span>
    </Col>
    <Col md={7}>
      {children}
      {helper && (
        <div className="mt-2" style={styles.helper}>
          {helper}
        </div>
      )}
    </Col>
  </Row>
);

const StoreCarouselForm: React.FC<StoreCarouselFormProps> = ({
  carousel,
  onCancel,
  onSaved,
}) => {
  const isEdit = carousel !== undefined;
  const storeId = localStorage.getItem(STORAGE_CONSTANTS.STORE_ID);
  const currentImageUrl = carousel ? getCarouselImageUrl(carousel) : '';

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<CarouselFormValues>({
    resolver: yupResolver(carouselSchema),
    context: { hasCurrentImage: !!carousel?.image },
    mode: 'onChange',
    defaultValues: {
      carousel_image_type:
        carousel?.carousel_image_type ?? StoreCarouselImageType.PRODUCT_IMAGE,
      title: carousel?.title ?? '',
      product: carousel?.product?.id ?? null,
      image: null,
      is_active: carousel?.is_active ?? true,
    },
  });

  const type = watch('carousel_image_type');
  const productId = watch('product');
  const imageFile = watch('image');
  const isProductType = type === StoreCarouselImageType.PRODUCT_IMAGE;

  // Product picker (screen state, not form values)
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Preview: the newly chosen file, else the carousel's current image
  const newImageUrl = useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : null),
    [imageFile]
  );
  useEffect(() => {
    return () => {
      if (newImageUrl) URL.revokeObjectURL(newImageUrl);
    };
  }, [newImageUrl]);

  // Load the first page of products, or search results (debounced)
  useEffect(() => {
    if (!isProductType || !storeId) return;
    const query = search.trim();
    const timer = setTimeout(
      async () => {
        setProductsLoading(true);
        try {
          if (query) {
            const response: any = await Search_Store_Product(query, storeId);
            setProducts(response?.data?.search_product_list ?? []);
          } else {
            const response: any = await Store_Product_Details(storeId, 1, '');
            setProducts(response?.data?.store_product_list ?? []);
          }
        } catch (error) {
          console.log('Error fetching store products', error);
          setProducts([]);
        } finally {
          setProductsLoading(false);
        }
      },
      query ? 300 : 0
    );
    return () => clearTimeout(timer);
  }, [isProductType, search, storeId]);

  const onSubmit = async (values: CarouselFormValues) => {
    try {
      if (isEdit) {
        // PATCH only what the user changed
        const payload: UpdateStoreCarouselPayload = {};
        if (dirtyFields.title) payload.title = values.title;
        if (dirtyFields.is_active) payload.is_active = values.is_active;
        if (dirtyFields.image && values.image) payload.image = values.image;
        if (dirtyFields.product && values.product != null) {
          payload.product = values.product;
        }
        if (Object.keys(payload).length > 0) {
          await UpdateStoreCarousel(carousel.id, payload);
          toast.success('Carousel updated');
        }
      } else {
        const base = { title: values.title, is_active: values.is_active };
        // The schema guarantees the field matching the type is set
        const payload: CreateStoreCarouselPayload = isProductType
          ? { ...base, product: values.product! }
          : { ...base, image: values.image! };
        await CreateStoreCarousel(payload);
        toast.success('Carousel created');
      }
      onSaved();
    } catch (error) {
      console.log('Error saving carousel', error);
      toast.error("Couldn't save the carousel");
    }
  };

  const selectedMissingFromList =
    isProductType &&
    productId != null &&
    productId === carousel?.product?.id &&
    !products.some((p) => p.id === productId);

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <div className="pb-3 border-bottom">
        <h5 className="mb-1" style={styles.heading}>
          {isEdit ? `Edit Carousel: ${carousel.title}` : 'Add Carousel'}
        </h5>
        <div style={styles.subtitle}>
          Customize display parameters and selected items.
        </div>
      </div>

      <FieldRow
        label="Carousel Type"
        required
        helper={
          isEdit
            ? "The type can't be changed after the carousel is created."
            : 'Feature one of your products, or upload your own banner image.'
        }
      >
        <Controller
          name="carousel_image_type"
          control={control}
          render={({ field }) => (
            <div
              className="d-inline-flex p-1 rounded-2 gap-1"
              style={styles.segmented}
              role="radiogroup"
              aria-label="Carousel type"
            >
              {TYPE_OPTIONS.map((option) => {
                const active = field.value === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className="btn btn-sm rounded-2 px-3"
                    style={active ? styles.segmentActive : styles.segment}
                    disabled={isEdit && !active}
                    onClick={() => field.onChange(option.value)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}
        />
      </FieldRow>

      <FieldRow
        label="Title"
        helper="Shown as the banner's title on your shop page."
      >
        <Form.Control
          size="sm"
          isInvalid={!!errors.title}
          {...register('title')}
        />
        <Form.Control.Feedback type="invalid">
          {errors.title?.message}
        </Form.Control.Feedback>
      </FieldRow>

      {isProductType ? (
        <FieldRow
          label="Product"
          required
          helper="The banner uses this product's image and links to it."
        >
          <InputGroup size="sm" className="mb-2">
            <InputGroup.Text className="bg-white text-secondary">
              <LuSearch size={14} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search products..."
              value={search}
              className="border-start-0"
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>

          {selectedMissingFromList && (
            <div className="d-flex align-items-center gap-2 mb-2 small text-secondary">
              {carousel?.product?.product_image && (
                <Image
                  src={getImageUrl(carousel.product.product_image)}
                  rounded
                  style={styles.productThumb}
                />
              )}
              Currently linked product (#{productId}). Search to change it.
            </div>
          )}

          <Controller
            name="product"
            control={control}
            render={({ field }) => (
              <div
                className={`border rounded-2 ${errors.product ? 'border-danger' : ''}`}
                style={styles.productList}
              >
                {productsLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center text-secondary small py-4">
                    No products found.
                  </div>
                ) : (
                  products.map((product) => {
                    const selected = product.id === field.value;
                    const imageUrl = getImageUrl(product.product_image);
                    return (
                      <label
                        key={product.id}
                        className="d-flex align-items-center gap-2 px-3 py-2 border-bottom mb-0"
                        style={{
                          cursor: 'pointer',
                          ...(selected ? styles.productRowSelected : {}),
                        }}
                      >
                        <Form.Check
                          type="radio"
                          name={field.name}
                          className="mb-0"
                          checked={selected}
                          onChange={() => field.onChange(product.id)}
                          onBlur={field.onBlur}
                        />
                        {imageUrl ? (
                          <Image
                            src={imageUrl}
                            rounded
                            style={styles.productThumb}
                          />
                        ) : (
                          <span
                            className="d-inline-flex align-items-center justify-content-center rounded bg-light text-secondary"
                            style={styles.productThumb}
                          >
                            <LuImageOff size={12} />
                          </span>
                        )}
                        <span
                          className="flex-grow-1 text-truncate"
                          style={styles.productName}
                        >
                          {product.product_name}
                        </span>
                        {product.price != null && (
                          <span
                            className="text-nowrap"
                            style={styles.productPrice}
                          >
                            रु. {product.price}
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
            )}
          />
          {errors.product && (
            <div className="invalid-feedback d-block">
              {errors.product.message}
            </div>
          )}
        </FieldRow>
      ) : (
        <FieldRow
          label="Image"
          required
          helper="Wide banner image, ideally 16:5 (e.g. 1600 × 500)."
        >
          {(newImageUrl || currentImageUrl) && (
            <Image
              src={newImageUrl || currentImageUrl}
              rounded
              className="d-block mb-2 border"
              style={styles.imagePreview}
            />
          )}
          <Controller
            name="image"
            control={control}
            render={({ field }) => (
              <Form.Control
                type="file"
                size="sm"
                accept="image/*"
                name={field.name}
                isInvalid={!!errors.image}
                onBlur={field.onBlur}
                onChange={(e) =>
                  field.onChange(
                    (e.target as HTMLInputElement).files?.[0] ?? null
                  )
                }
              />
            )}
          />
          <Form.Control.Feedback type="invalid" className="d-block">
            {errors.image?.message}
          </Form.Control.Feedback>
          {isEdit && currentImageUrl && !imageFile && (
            <div className="mt-1" style={styles.helper}>
              Leave empty to keep the current image.
            </div>
          )}
        </FieldRow>
      )}

      <FieldRow
        label="Is active"
        helper="Toggle to show/hide this carousel on your shop page."
      >
        <Form.Check
          type="switch"
          id="carousel-is-active"
          className="form-switch-success mb-0"
          label="Active on storefront"
          {...register('is_active')}
        />
      </FieldRow>

      <div className="d-flex justify-content-end gap-2 pt-3">
        <Button
          variant="light"
          size="sm"
          className="border px-3"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          className="px-4"
          disabled={isSubmitting}
        >
          {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Save'}
        </Button>
      </div>
    </Form>
  );
};

export default StoreCarouselForm;
